/**
 * Thin shell: composes `useDockviewManager` and the two Context providers.
 * Business concerns (content rendering, slot composition) live here; all
 * dockview wiring lives in the `dockview/` module (架构方案 §5.7).
 */
import {
  type IModuleService,
  type LayoutArea,
  type LayoutSchema,
  type ResolvedView,
} from '@hibro/shared';
import { DockviewReact, themeLight } from 'dockview-react';
import { type ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';

import type { UiRegistry } from '../runtime/ui-registry';
import type { DetachBridge } from '../surfaces/detach';
import type { ExternalBridge } from '../surfaces/external-surface';
import type { IframeBridge } from '../surfaces/iframe-surface';
import { SurfaceView } from '../surfaces/surface-host';
import {
  AreaContentContext,
  AuxiliaryBarActions,
  CustomGroupDragGhost,
  DOCK_COMPONENTS,
  DockviewTabHost,
  EditorSingletonTitleContext,
  SurfaceHostContext,
  TAB_COMPONENTS,
  TabContentCache,
  TabContentCacheContext,
  TabDetachContext,
  useDockviewManager,
  withAreaOccupied,
} from './dockview';
import { TabControllerOutlet } from './dockview/tab-controller-outlet';
import { TabHostRuntimeContext } from './dockview/tab-host-context';
import { WorkbenchLayoutContext } from './layout-context';

export type { ToggleableArea } from './layout-context';
export type { WorkbenchLayoutApi } from './layout-context';
export { useWorkbenchLayout } from './layout-context';

/**
 * Dockable content areas presented as floating surface cards (IM reskin, §5.7).
 * Chrome areas (header / activityBar / statusBar) are excluded so they render
 * flush and blend with the workbench backdrop.
 */
const CARD_AREAS = new Set<LayoutArea>([
  'editor',
  'auxiliaryBar',
  'sidebar',
  'panel',
]);

/** Stable empty default for `moduleHiddenAreas` (avoids per-render churn). */
const EMPTY_HIDDEN_AREAS: readonly LayoutArea[] = [];

export interface WorkbenchLayoutProps {
  schema: LayoutSchema;
  views: ResolvedView[];
  /**
   * The *full* contributed view set. `views` is the inline-docked subset
   * (excludes editor / activityBar / overlay / gate / auxiliaryBar). `allViews`
   * is the complete list: occupancy of `auxiliaryBar`, TabHost routing, and
   * `renderSurface` lookup by viewId. Defaults to `views`.
   */
  allViews?: ResolvedView[];
  registry: UiRegistry;
  moduleService: IModuleService;
  /**
   * The active module (owner of the active editor view). Drives module-scoped
   * default area visibility together with {@link moduleHiddenAreas} (§5.7).
   */
  activeModuleId?: string;
  /**
   * Areas the active module wants hidden by default (its `hiddenAreas`
   * blacklist). A soft default the user can still toggle open; see
   * `useDockviewManager` (§5.7).
   */
  moduleHiddenAreas?: readonly LayoutArea[];
  /** Optional chrome injected by the workbench into specific areas. */
  slots?: Partial<Record<LayoutArea, ReactNode>>;
  /**
   * Workbench chrome for the startup prompt (见 23). Drawn on the frame, not
   * inside the sidebar panel: that column is only created when a module
   * occupies it, and none does.
   */
  sidebarOverlay?: ReactNode;
  /** Pop a detachable view into its own window (§5.8); forwarded to surfaces. */
  onDetachView?: (
    viewId: string,
    moduleId: string,
    instanceKey?: string,
  ) => void;
  /**
   * Serialized handles of migrated surfaces (§5.8); see `detachedSurfaceKey`.
   * An instance tab whose handle is in here shows a placeholder instead of its
   * content, which now lives in another window.
   */
  detachedSurfaces?: ReadonlySet<string>;
  /** Attach a detached view back into this window (§5.8). */
  onAttachView?: (viewId: string, instanceKey?: string) => void;
  /** Focus the window hosting a detached view (§5.8). */
  onFocusDetached?: (viewId: string, instanceKey?: string) => void;
  /** Open/focus a view instance (§5.7); backs a hosted view's OpenViewRequest routing. */
  onOpenView?: (viewId: string, instanceKey?: string) => void;
  /** Bridge for in-DOM iframe surfaces (见 9); forwarded to surfaces. */
  iframeBridge?: IframeBridge;
  /** Bridge for external (executable) surfaces (见 10); forwarded to surfaces. */
  externalBridge?: ExternalBridge;
  /** Detach mechanism bridge (§5.8); forwarded to surfaces as `ViewProps.detach`. */
  detachBridge?: DetachBridge;
  /** Stable module-facing host for the auxiliaryBar native tab area. */
  tabHost: DockviewTabHost;
  /**
   * Host for the editor area's native tab strip — one tab per document-like view
   * instance (§5.7 / 见 20 二期). Separate host, same class: each `DockviewTabHost`
   * is bound to one area, and the two areas have different occupancy policies
   * (the auxiliaryBar projects one module at a time, the editor does not).
   */
  editorTabHost?: DockviewTabHost;
  /**
   * When false, dockview restore/build waits: occupancy of `auxiliaryBar` is
   * unknown until `listContributions` settles, and gating on the empty first
   * paint would disable the area on desktop then fight a restored layout.
   */
  contributionsReady?: boolean;
  /** Subscribe to contribution-point changes (见 17); backs `ViewProps.contributions`. */
  subscribeContributionsChanged?: (listener: () => void) => () => void;
  /**
   * Title painted on the editor's structural-anchor tab once instance tabs
   * lift the strip — the translated name of the currently active rail module.
   */
  editorSingletonTitle?: string;
}

/**
 * Schema-driven workbench shell (架构方案 §5.7).
 *
 * The whole workbench is one serialized dockview using the edge-group shell
 * (方式②). `header` / `statusBar` are full-width chrome pinned in the shell's
 * top / bottom slots *outside* the central gridview, framing it. The central
 * gridview's root is the center itself:
 *   center = horizontal [ activityBar, sidebar, [editor, panel], auxiliaryBar ]
 * `activityBar` is the locked leftmost column; the dockable regions (`sidebar` /
 * `editor` (+`panel`) / `auxiliaryBar`) split & nest freely. Because chrome
 * lives outside the gridview, the center's outer (root) edges are clean — used
 * for outer docking, gated to left/right only via `ROOT_DOCK_EDGES`. Each area's
 * drag / resize / drop behaviour comes from its `AREA_SPECS` entry. Contributed
 * views slot into each area via `SurfaceView`; an empty area shows a placeholder.
 *
 * All dockview lifecycle, capability config, visibility toggle, reset and
 * persistence logic is encapsulated in `useDockviewManager` from the
 * `dockview/` module. External consumers (header buttons, command palette,
 * etc.) access the public API via `useWorkbenchLayout()`.
 */
export function WorkbenchLayout({
  schema,
  views,
  allViews,
  registry,
  moduleService,
  activeModuleId,
  moduleHiddenAreas,
  slots,
  sidebarOverlay,
  onDetachView,
  detachedSurfaces,
  onAttachView,
  onFocusDetached,
  onOpenView,
  iframeBridge,
  externalBridge,
  detachBridge,
  tabHost,
  editorTabHost,
  contributionsReady = true,
  subscribeContributionsChanged,
  editorSingletonTitle,
}: WorkbenchLayoutProps): ReactNode {
  const dockviewContainerRef = useRef<HTMLDivElement>(null);
  const renderViews = useCallback(
    (area: LayoutArea): ReactNode[] => {
      return views
        .filter((view) => view.area === area)
        .map((view) => (
          <SurfaceView
            key={view.id}
            viewId={view.id}
            moduleId={view.moduleId}
            registry={registry}
            moduleService={moduleService}
            detachable={view.detachable}
            onDetach={onDetachView}
            kind={view.kind}
            url={view.url}
            iframeBridge={iframeBridge}
            detachBridge={detachBridge}
          />
        ));
    },
    [views, registry, moduleService, onDetachView, iframeBridge, detachBridge],
  );

  // Render a single view *instance* into a dockview tab (§5.7 视图多实例): the
  // `surfaceTab` panel in a tabbed area (auxiliaryBar) consumes this via
  // SurfaceHostContext. Resolves the view declaration by `viewId`, then mounts a
  // SurfaceView keyed by the full instance id so switching between instances of
  // the same view remounts cleanly. Kept module-agnostic — the area knows only
  // «render this instance», not what the instance means (e.g. a chat session).
  const renderSurface = useCallback(
    (viewId: string, instanceKey?: string): ReactNode => {
      // Resolve against the full view set: layout `views` omit editor /
      // activityBar / overlay / gate / auxiliaryBar (those are hosted
      // elsewhere), so a tabbed area looks up declarations in `allViews`.
      const view = (allViews ?? views).find(
        (candidate) => candidate.id === viewId,
      );
      if (!view) {
        return null;
      }
      return (
        <SurfaceView
          key={instanceKey === undefined ? viewId : `${viewId}::${instanceKey}`}
          viewId={view.id}
          moduleId={view.moduleId}
          instanceKey={instanceKey}
          registry={registry}
          moduleService={moduleService}
          onOpenView={onOpenView}
          detachable={view.detachable}
          onDetach={onDetachView}
          kind={view.kind}
          url={view.url}
          externalEntry={view.externalEntry}
          iframeBridge={iframeBridge}
          externalBridge={externalBridge}
          detachBridge={detachBridge}
          subscribeContributionsChanged={subscribeContributionsChanged}
        />
      );
    },
    [
      allViews,
      views,
      registry,
      moduleService,
      onOpenView,
      onDetachView,
      iframeBridge,
      externalBridge,
      detachBridge,
      subscribeContributionsChanged,
    ],
  );

  // Content for an area: its injected slot + contributed views, or a labeled
  // placeholder so an otherwise-empty area is still visible. A tabbed host area
  // (auxiliaryBar) is fed an empty-fragment slot by the caller and keeps its
  // instances out of `views`, so it renders nothing inline here — its sessions
  // come through `openAuxTab` → `SurfaceHostContext` as native dockview tabs.
  const areaContent = useCallback(
    (area: LayoutArea): ReactNode => {
      const slot = slots?.[area];
      const viewNodes = renderViews(area);
      const inner =
        slot != null || viewNodes.length > 0 ? (
          <>
            {slot}
            {viewNodes}
          </>
        ) : (
          <div className="text-center">{area}</div>
        );

      // IM-style presentation: dockable content panes float as rounded surface
      // cards over the transparent dockview backdrop (gaps come from the wrapper
      // padding); chrome areas (header / activityBar / statusBar) render flush so
      // they blend with the backdrop.
      if (!CARD_AREAS.has(area)) {
        return inner;
      }
      return <>{inner}</>;
    },
    [slots, renderViews],
  );

  const occupiedAuxiliaryBar = (allViews ?? views).some(
    (view) => view.area === 'auxiliaryBar',
  );
  const effectiveSchema = useMemo(
    () => withAreaOccupied(schema, 'auxiliaryBar', occupiedAuxiliaryBar),
    [schema, occupiedAuxiliaryBar],
  );

  const { onReady, layoutApi } = useDockviewManager(
    effectiveSchema,
    activeModuleId,
    moduleHiddenAreas ?? EMPTY_HIDDEN_AREAS,
    dockviewContainerRef,
    contributionsReady,
  );
  const handleReady = useCallback(
    (event: Parameters<typeof onReady>[0]) => {
      onReady(event);
      tabHost.attach(event.api);
      editorTabHost?.attach(event.api);
    },
    [onReady, tabHost, editorTabHost],
  );
  const tabHostRuntime = useMemo(
    () => ({
      host: tabHost,
      editorHost: editorTabHost,
      views: allViews ?? views,
      registry,
      executeCommand: (id: string, ...args: unknown[]) =>
        moduleService.executeCommand(id, ...args),
    }),
    [allViews, views, registry, tabHost, editorTabHost, moduleService],
  );
  const tabDetach = useMemo(
    () => ({
      detachedSurfaces,
      onDetachView,
      onAttachView,
      onFocusDetached,
    }),
    [detachedSurfaces, onDetachView, onAttachView, onFocusDetached],
  );
  // Keep-alive budget for instance-tab contents (§5.7 视图缓存), one per window.
  // Lives here rather than in the workbench: which tab contents are mounted is a
  // property of this dockview presentation, and the panels that consume it are
  // rendered (through portals) as children of `DockviewReact` below.
  const tabContentCache = useMemo(() => new TabContentCache(), []);
  useEffect(() => {
    if (!subscribeContributionsChanged) {
      return;
    }
    // Same conservative eviction the editor cache does (见 12/17): a module can
    // be updated in place, and a kept-alive tree from its previous UI bundle
    // must not outlive it.
    return subscribeContributionsChanged(() =>
      tabContentCache.evictOffScreen(),
    );
  }, [subscribeContributionsChanged, tabContentCache]);

  const hostAwareLayoutApi = useMemo(
    () => ({
      ...layoutApi,
      reset: () => {
        // Both hosts queue intents across the reset, or a tab opened while the
        // layout is being rebuilt lands on a dockview api that is about to be
        // cleared.
        tabHost.beginRestore();
        editorTabHost?.beginRestore();
        try {
          layoutApi.reset();
        } finally {
          tabHost.endRestore();
          editorTabHost?.endRestore();
        }
        // The rail's active view is unchanged across reset, but the rebuilt
        // editor group does not activate its anchor. Leave the singleton
        // EditorSlot in front so the `renderer: 'always'` overlay is visible
        // for the post-reset shell remeasure.
        editorTabHost?.showAnchor();
      },
    }),
    [layoutApi, tabHost, editorTabHost],
  );

  return (
    <WorkbenchLayoutContext.Provider value={hostAwareLayoutApi}>
      <AreaContentContext.Provider value={areaContent}>
        <SurfaceHostContext.Provider value={renderSurface}>
          <TabContentCacheContext.Provider value={tabContentCache}>
            <TabHostRuntimeContext.Provider value={tabHostRuntime}>
              <TabDetachContext.Provider value={tabDetach}>
                <EditorSingletonTitleContext.Provider
                  value={editorSingletonTitle ?? ''}
                >
                  <TabControllerOutlet
                    host={tabHost}
                    views={allViews ?? views}
                    registry={registry}
                    executeCommand={tabHostRuntime.executeCommand}
                  />
                  <div
                    ref={dockviewContainerRef}
                    className="relative h-full w-full text-foreground"
                  >
                    <DockviewReact
                      components={DOCK_COMPONENTS}
                      tabComponents={TAB_COMPONENTS}
                      // Right-aligned header actions. dockview mounts this once
                      // per group; it self-scopes to the auxiliaryBar group and
                      // renders an add-tab / history / hide-area control there,
                      // nothing elsewhere (§5.7).
                      rightHeaderActionsComponent={AuxiliaryBarActions}
                      // Hide dockview's default "Multiple Panels (N)" group
                      // ghost: auxiliaryBar includes a structural anchor panel
                      // that is not a user-visible tab, so the count misleads.
                      groupDragGhostComponent={CustomGroupDragGhost}
                      theme={themeLight}
                      className="app-dockview app-dockview--no-close"
                      // Root-edge drop targets stay at dockview's default
                      // (enabled). They bind to the central gridview element —
                      // header / statusBar live in the edge-group shell
                      // *outside* it — so the four outer edges fall inside the
                      // chrome frame. Direction is gated to left/right in
                      // `useDockviewManager` (onWillShowOverlay veto; see
                      // ROOT_DOCK_EDGES); top/bottom are temporarily disabled.
                      onReady={handleReady}
                    />
                    {sidebarOverlay ? (
                      <div className="pointer-events-none absolute inset-0 z-30">
                        {sidebarOverlay}
                      </div>
                    ) : null}
                  </div>
                </EditorSingletonTitleContext.Provider>
              </TabDetachContext.Provider>
            </TabHostRuntimeContext.Provider>
          </TabContentCacheContext.Provider>
        </SurfaceHostContext.Provider>
      </AreaContentContext.Provider>
    </WorkbenchLayoutContext.Provider>
  );
}
